/**
 * Keeps the widget's sticky topbar working inside storefronts that wrap the embed in a clipping
 * container.
 *
 * `position: sticky` pins to the nearest *scroll container*, and `overflow: hidden` makes an element
 * one — even though nobody can scroll it. So a theme that wraps our embed in a
 * `<div style="overflow:hidden">` (a very common way to kill a few pixels of horizontal scroll)
 * silently re-points our topbar at that div instead of the viewport. The bar then never pins: it
 * rides the page up, slides under the storefront's own sticky header and disappears. `stickyOffset`
 * looks broken because the offset it sets has nothing to act on.
 *
 * `overflow: clip` clips exactly the same way but is NOT a scroll container, so swapping it in
 * restores sticky while preserving what the theme wanted. We do that to the ancestors between the
 * embed and `<body>` — the theme's own wrapper divs — and nothing else.
 *
 * Observed in the wild: givnberlin.com wraps the block in `.container-fluid` at `overflow: hidden`
 * and the topbar vanishes; wheat.de drops it straight into `<main>` with no clipping ancestor and
 * the identical config works. Same widget, same setting, different host wrapper.
 */

/** A horizontal or vertical overflow axis. */
export type Axis = 'x' | 'y'

/** What we need to know about one ancestor to decide whether its clipping is safe to convert. */
export interface OverflowFacts {
  /** Computed `overflow-x`. */
  overflowX: string
  /** Computed `overflow-y`. */
  overflowY: string
  /** Whether content actually overflows horizontally (`scrollWidth > clientWidth`). */
  scrollsHorizontally: boolean
  /** Whether content actually overflows vertically (`scrollHeight > clientHeight`). */
  scrollsVertically: boolean
}

const SCROLLABLE = new Set(['auto', 'scroll'])

/**
 * Which axes of one element may be switched from `hidden` to `clip`.
 *
 * Only `hidden` is ever converted — `auto`/`scroll` are deliberate, usable scrollers and are left
 * alone. The subtlety is that an author who writes `overflow-x: hidden` alone gets a *computed*
 * `overflow-y: auto`, so "the other axis is auto" cannot by itself mean "real scroller". We
 * distinguish by whether the element genuinely overflows in that direction: if it does, something
 * scrolls there and we refuse (a `clip`/`auto` pair computes back to `hidden`, which would silently
 * disable it); if it does not, the `auto` is just the side effect and converting is safe.
 */
export function axesToUnclip(facts: OverflowFacts): Axis[] {
  if (SCROLLABLE.has(facts.overflowX) && facts.scrollsHorizontally) return []
  if (SCROLLABLE.has(facts.overflowY) && facts.scrollsVertically) return []

  const axes: Axis[] = []
  if (facts.overflowX === 'hidden') axes.push('x')
  if (facts.overflowY === 'hidden') axes.push('y')
  return axes
}

/** Read the overflow facts the decision needs off a live element. */
function readFacts(el: Element): OverflowFacts {
  const cs = getComputedStyle(el)
  return {
    overflowX: cs.overflowX,
    overflowY: cs.overflowY,
    scrollsHorizontally: el.scrollWidth > el.clientWidth,
    scrollsVertically: el.scrollHeight > el.clientHeight,
  }
}

/**
 * Convert clipping ancestors of `host` from `overflow: hidden` to `overflow: clip` so our sticky
 * topbar pins to the viewport again. Returns a function that puts every touched element back.
 *
 * Stops below `<body>` on purpose. `overflow` on `<body>`/`<html>` propagates to the viewport rather
 * than making the body a scroll container, so it does not break sticky — while overwriting it would
 * fight whatever the storefront does for scroll-locking behind its own menus and modals.
 *
 * A no-op where `overflow: clip` is unsupported (Safari < 16): the storefront keeps today's
 * behaviour rather than getting a broken halfway state.
 */
export function unclipStickyAncestors(host: Element): () => void {
  if (typeof window === 'undefined') return () => {}
  if (!window.CSS?.supports?.('overflow', 'clip')) return () => {}

  const undo: Array<() => void> = []

  for (let el = host.parentElement; el && el !== document.body; el = el.parentElement) {
    for (const axis of axesToUnclip(readFacts(el))) {
      const property = axis === 'x' ? 'overflow-x' : 'overflow-y'
      const previous = (el as HTMLElement).style.getPropertyValue(property)
      const priority = (el as HTMLElement).style.getPropertyPriority(property)
      const target = el as HTMLElement

      // The theme's value usually comes from a stylesheet, so an inline `clip` wins without !important.
      target.style.setProperty(property, 'clip')
      undo.push(() => {
        if (previous) target.style.setProperty(property, previous, priority)
        else target.style.removeProperty(property)
      })
    }
  }

  return () => {
    for (const restore of undo) restore()
  }
}
